/**
 * Points and pixels.
 *
 * The document stores lengths in page pixels at 96 dpi (1 in = 96 px), so 1 pt = 96 / 72 px. Publisher talks in points
 * for everything typographic — font sizes, space before / after, exact line spacing, kerning — so the UI shows and accepts
 * points and converts at the edges; existing publications keep their values (16 px simply reads as 12 pt).
 *
 * Line spacing (`lineHeight` on a box or paragraph) is either
 *   - a number: a multiple of the font size ("1.2 sp" in Publisher's words), or
 *   - an exact height, kept as a CSS length string such as "16px" ("Exactly 12 pt").
 * CSS renders both as written; everything that reads a line height goes through the helpers below.
 */
export const PX_PER_PT = 96 / 72;
export const PT_PER_PX = 72 / 96;

const round = (v, digits) => { const f = 10 ** digits; return Math.round(v * f) / f; };

/** Page px → points, rounded (2 decimals by default: 15 px → 11.25 pt, 16 px → 12 pt). */
export const pxToPt = (px, digits = 2) => round((Number(px) || 0) * PT_PER_PX, digits);
/** Points → page px, rounded to 3 decimals (12 pt → 16 px, 10.5 pt → 14 px, 11 pt → 14.667 px; reads back as 11 pt). */
export const ptToPx = (pt) => round((Number(pt) || 0) * PX_PER_PT, 3);
/** "12", "10.5", "11.25" — a point value for a field, without trailing zeros. */
export const fmtPt = (px, digits = 2) => String(pxToPt(px, digits));

/** Publisher's size list, in points (the size box, Grow / Shrink Font). */
export const FONT_SIZES_PT = [6, 7, 8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72, 96];

/* ---------- line spacing: multiple ("sp") or exact ("…px" = Exactly N pt) ---------- */
export const isExactLineHeight = (v) => typeof v === 'string' && /^\s*-?[\d.]+px\s*$/.test(v);
/** Exact line height in page px (undefined for a multiple). */
export const exactLineHeightPx = (v) => (isExactLineHeight(v) ? parseFloat(v) : undefined);
/** An exact line height from points: exactLineHeight(12) → "16px". */
export const exactLineHeight = (pt) => `${round(ptToPx(pt), 3)}px`;
/** The multiple of a line height value (an exact one relative to the given font size). */
export const lineHeightMultiple = (v, fontSizePx = 16) => {
  if (isExactLineHeight(v)) return parseFloat(v) / (fontSizePx || 16);
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 1.2;
};
/** "1.5" or "Exactly 12 pt" — how a line height reads in a tooltip or a list. */
export const lineHeightLabel = (v) => (isExactLineHeight(v) ? `Exactly ${fmtPt(parseFloat(v))} pt` : String(round(Number(v) || 1.2, 3)));
/** A line height for a rendering scaled by `k` (canvas zoom, autofit): an exact height scales, a multiple is as it is. */
export const scaleLineHeight = (v, k = 1) => (isExactLineHeight(v) ? `${round(parseFloat(v) * (k || 1), 3)}px` : v);

/* ---------- canvas (ruler / measurement) units ---------- */
/** Ruler and measurement units, in the order View ▸ Units (and the ruler corner) cycle through them; inches are the default. */
export const CANVAS_UNITS = ['in', 'cm', 'pt', 'px'];
export const nextCanvasUnit = (u) => CANVAS_UNITS[(CANVAS_UNITS.indexOf(u) + 1) % CANVAS_UNITS.length];
/** How a unit reads in a tooltip. */
export const CANVAS_UNIT_NAMES = { in: 'inches', cm: 'centimetres', mm: 'millimetres', pt: 'points', px: 'pixels' };
